import { AxiosError } from 'axios';

export interface NotificationService {
  showSuccess: (title: string, message: string, duration?: number) => void;
  showError: (title: string, message: string, persistent?: boolean) => void;
  showWarning: (title: string, message: string, duration?: number) => void;
  showInfo: (title: string, message: string, duration?: number) => void;
  showActionNotification: (
    title: string,
    message: string,
    actionLabel: string,
    actionCallback: () => void,
    type?: 'success' | 'error' | 'warning' | 'info'
  ) => void;
  showMaintenanceAlert: (
    title: string,
    message: string,
    startTime?: Date,
    endTime?: Date,
    dismissible?: boolean
  ) => void;
  handleApiError: (error: unknown, context?: string) => void;
}

class NotificationServiceImpl implements NotificationService {
  private notificationHelpers: any = null;
  private translateFunction: ((key: string, params?: Record<string, string>) => string) | null = null;

  setNotificationHelpers(helpers: any) {
    this.notificationHelpers = helpers;
  }

  setTranslateFunction(t: (key: string, params?: Record<string, string>) => string) {
    this.translateFunction = t;
  }

  private t(key: string, params?: Record<string, string>): string {
    return this.translateFunction ? this.translateFunction(key, params) : key;
  }

  showSuccess(title: string, message: string, duration?: number) {
    this.notificationHelpers?.showSuccess(title, message, duration);
  }

  showError(title: string, message: string, persistent = true) {
    this.notificationHelpers?.showError(title, message, persistent);
  }

  showWarning(title: string, message: string, duration?: number) {
    this.notificationHelpers?.showWarning(title, message, duration);
  }

  showInfo(title: string, message: string, duration?: number) {
    this.notificationHelpers?.showInfo(title, message, duration);
  }

  showActionNotification(
    title: string,
    message: string,
    actionLabel: string,
    actionCallback: () => void,
    type: 'success' | 'error' | 'warning' | 'info' = 'info'
  ) {
    this.notificationHelpers?.showActionNotification(title, message, actionLabel, actionCallback, type);
  }

  showMaintenanceAlert(
    title: string,
    message: string,
    startTime?: Date,
    endTime?: Date,
    dismissible = true
  ) {
    this.notificationHelpers?.showMaintenanceAlert(title, message, startTime, endTime, dismissible);
  }

  handleApiError(error: unknown, context = 'Operation') {
    if (!this.notificationHelpers) return;

    let title = this.t('notifications.errors.operationFailed', { context });
    let message = this.t('notifications.errors.unexpectedError');

    if (error instanceof AxiosError) {
      if (error.response) {
        const status = error.response.status;
        const data = error.response.data;

        switch (status) {
          case 400:
            title = this.t('notifications.errors.invalidRequest');
            message = data?.detail || this.t('notifications.errors.invalidRequestMessage');
            break;
          case 401:
            title = this.t('notifications.errors.authenticationRequired');
            message = this.t('notifications.errors.authenticationMessage');
            break;
          case 403:
            title = this.t('notifications.errors.accessDenied');
            message = this.t('notifications.errors.accessDeniedMessage');
            break;
          case 404:
            title = this.t('notifications.errors.notFound');
            message = this.t('notifications.errors.notFoundMessage');
            break;
          case 409:
            title = this.t('notifications.errors.conflict');
            message = data?.detail || this.t('notifications.errors.conflictMessage');
            break;
          case 413:
            title = this.t('notifications.errors.fileTooLarge');
            message = this.t('notifications.errors.fileTooLargeMessage');
            break;
          case 422:
            title = this.t('notifications.errors.validationError');
            if (data?.detail && Array.isArray(data.detail)) {
              message = data.detail.map((err: any) => err.msg).join(', ');
            } else {
              message = data?.detail || this.t('notifications.errors.validationMessage');
            }
            break;
          case 500:
            title = this.t('notifications.errors.serverError');
            message = this.t('notifications.errors.serverErrorMessage');
            break;
          default:
            title = this.t('notifications.errors.operationFailed', { context });
            message = data?.detail || this.t('notifications.errors.requestFailedWithStatus', { status: status.toString() });
        }
      } else if (error.request) {
        title = this.t('notifications.errors.networkError');
        message = this.t('notifications.errors.networkErrorMessage');
      }
    } else if (error instanceof Error) {
      message = error.message;
    }

    this.showError(title, message);
  }

  // Success messages for common operations
  showOperationSuccess(operation: string, item?: string) {
    const defaultItems = {
      create: item || this.t('notifications.operations.defaultItem'),
      update: item || this.t('notifications.operations.defaultItem'),
      delete: item || this.t('notifications.operations.defaultItem'),
      save: item || this.t('notifications.operations.defaultChanges'),
      upload: item || this.t('notifications.operations.defaultFile'),
      download: item || this.t('notifications.operations.defaultFile'),
      export: item || this.t('notifications.operations.defaultData'),
      import: item || this.t('notifications.operations.defaultData'),
      login: '',
      logout: '',
    };

    let message: string;
    const operationItem = defaultItems[operation as keyof typeof defaultItems];

    if (operation === 'login') {
      message = this.t('notifications.operations.login');
    } else if (operation === 'logout') {
      message = this.t('notifications.operations.logout');
    } else if (operationItem !== undefined) {
      message = this.t(`notifications.operations.${operation}`, { item: operationItem });
    } else {
      message = this.t('notifications.operations.completed', { operation });
    }

    this.showSuccess(this.t('notifications.operations.success'), message);
  }
}

export const notificationService = new NotificationServiceImpl();
export default notificationService;